import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  calcRestartFee,
  formatRestartMarkdown,
  PROJECT_STAGE,
  EVENT_TYPE,
} from '../lib/restartFee';

// Two variants share the same logic and visual language. The public version
// is embedded on /terms and shows only what a client cares about. The admin
// version (rendered on /admin/restart-calc) exposes the extra fields Sander
// uses internally (revisions, support hours) plus a copy-to-clipboard button
// that emits an invoice-ready Markdown breakdown.

const STAGE_OPTIONS = [
  {
    value: PROJECT_STAGE.discovery,
    label: 'Discovery / pre-deposit',
    hint: 'Briefing happened, no deposit paid yet.',
  },
  {
    value: PROJECT_STAGE.preMid,
    label: 'Pre-midpoint',
    hint: 'Deposit (30%) paid, midpoint review not yet reached.',
  },
  {
    value: PROJECT_STAGE.postMid,
    label: 'Post-midpoint',
    hint: 'Midpoint (40%) paid, before final delivery.',
  },
];

const EVENT_OPTIONS = [
  { value: EVENT_TYPE.delay, label: 'Delay (≥ 2 weeks)', hint: 'Project paused on the client side.' },
  { value: EVENT_TYPE.cancel, label: 'Cancellation', hint: 'Project terminated by the client.' },
  { value: EVENT_TYPE.scopeChange, label: 'Scope change', hint: 'Out-of-brief work — quoted separately.' },
];

const fmtEuro = (n) => `€${(n || 0).toLocaleString('nl-BE').replace(/,/g, '.')}`;

const policyHash = (ref) => (ref ? `#${ref}` : '');

const RadioGroup = ({ name, options, value, onChange, columns = 'sm:grid-cols-3' }) => (
  <div className={`grid grid-cols-1 ${columns} gap-2.5 sm:gap-3`}>
    {options.map((opt) => {
      const isOn = value === opt.value;
      return (
        <label
          key={opt.value}
          className={`flex flex-col gap-1 rounded-xl border p-3.5 cursor-pointer transition-all duration-200 ${
            isOn
              ? 'bg-mainkleur text-licht border-mainkleur shadow-md shadow-mainkleur/20'
              : 'bg-white/40 text-mainkleur border-mainkleur/20 hover:border-mainkleur/60'
          }`}
        >
          <span className="flex items-center gap-2">
            <input
              type="radio"
              name={name}
              value={opt.value}
              checked={isOn}
              onChange={() => onChange(opt.value)}
              className="sr-only"
            />
            <span
              aria-hidden="true"
              className={`inline-flex w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                isOn ? 'border-licht' : 'border-mainkleur/40'
              }`}
            >
              {isOn && <span className="m-auto w-2 h-2 rounded-full bg-licht" />}
            </span>
            <span className="font-bricolage font-extrabold text-sm sm:text-base uppercase tracking-tight">
              {opt.label}
            </span>
          </span>
          <span className={`font-space text-xs sm:text-[13px] leading-snug ${isOn ? 'text-licht/75' : 'text-mainkleur/65'}`}>
            {opt.hint}
          </span>
        </label>
      );
    })}
  </div>
);

const NumberField = ({
  id,
  label,
  value,
  onChange,
  suffix,
  hint,
  step = 1,
  min = 0,
  labelClassName = 'text-mainkleur',
  inputClassName = 'text-mainkleur border-mainkleur',
  hintClassName = 'text-mainkleur/55',
}) => (
  <div className="flex flex-col gap-1.5">
    <label htmlFor={id} className={`font-space ${labelClassName} text-xs uppercase tracking-[0.18em]`}>
      {label}
    </label>
    <div className="relative flex items-center">
      <input
        id={id}
        type="number"
        inputMode="decimal"
        min={min}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full bg-transparent font-space-grotesk font-bold text-base sm:text-lg pb-1.5 pr-12 border-b outline-none focus:border-[#f97b22] tabular-nums ${inputClassName}`}
      />
      {suffix && (
        <span className="absolute right-1 bottom-1.5 text-mainkleur/55 font-space text-sm">
          {suffix}
        </span>
      )}
    </div>
    {hint && <span className={`font-space ${hintClassName} text-xs leading-snug`}>{hint}</span>}
  </div>
);

const RestartCalculator = ({ variant = 'public' }) => {
  const isAdmin = variant === 'admin';

  const [contractTotal, setContractTotal] = useState('3500');
  const [stage, setStage] = useState(PROJECT_STAGE.preMid);
  const [event, setEvent] = useState(EVENT_TYPE.delay);
  const [delayWeeks, setDelayWeeks] = useState('3');
  const [productionDonePct, setProductionDonePct] = useState(40);
  const [extraRevisions, setExtraRevisions] = useState('0');
  const [outOfWindowHours, setOutOfWindowHours] = useState('0');
  const [copied, setCopied] = useState(false);

  const result = useMemo(
    () =>
      calcRestartFee({
        contractTotal,
        stage,
        event,
        delayWeeks,
        productionDonePct,
        extraRevisions: isAdmin ? extraRevisions : 0,
        outOfWindowHours: isAdmin ? outOfWindowHours : 0,
      }),
    [contractTotal, stage, event, delayWeeks, productionDonePct, extraRevisions, outOfWindowHours, isAdmin]
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(formatRestartMarkdown(result));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="rounded-3xl bg-mainkleur text-licht p-6 sm:p-10 lg:p-12">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 sm:mb-10">
        <div>
          <span className="font-space text-licht/55 text-xs uppercase tracking-[0.22em]">
            {isAdmin ? 'Admin · restart-fee tool' : 'Restart-fee estimator'}
          </span>
          <h2 className="mt-2 font-bricolage font-extrabold text-licht text-[28px] sm:text-[40px] lg:text-[52px] leading-[0.95] tracking-tight uppercase">
            {isAdmin ? 'Project pause / cancel calculator' : 'What happens if a project pauses or stops?'}
          </h2>
          <p className="mt-3 font-space text-licht/70 text-base sm:text-lg leading-[1.5] max-w-[640px]">
            {isAdmin
              ? 'Internal cost-of-stop tool. Wires straight to the ToS clauses on /terms — every line item carries a policyRef so the math is auditable.'
              : 'A rough estimate of how the deposit / midpoint / final structure plays out if the project gets paused or cancelled. Final amounts always confirmed in writing.'}
          </p>
        </div>
        {isAdmin && (
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-2 self-start sm:self-end rounded-full bg-[#f97b22] hover:bg-[#fe9043] text-mainkleur px-5 py-2.5 font-bricolage font-semibold text-sm uppercase tracking-tight transition-all duration-200"
          >
            {copied ? 'Copied ✓' : 'Copy Markdown breakdown'}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
        {/* Inputs */}
        <div className="flex flex-col gap-6 sm:gap-7">
          <div className="bg-licht/5 rounded-2xl p-5 sm:p-6 border border-licht/15">
            <div className="font-space text-licht/55 text-xs uppercase tracking-[0.22em] mb-4">
              Contract total
            </div>
            <NumberField
              id="rc-total"
              label="Excl. 21% VAT"
              value={contractTotal}
              onChange={setContractTotal}
              suffix="€"
              hint="Use the figure from the signed quote."
              step={50}
              labelClassName="text-[#d9d9d9]"
              inputClassName="text-[#e4dbd2] border-[#f97b22]"
              hintClassName="text-[#d9d9d9]"
            />
          </div>

          <div className="bg-licht/5 rounded-2xl p-5 sm:p-6 border border-licht/15">
            <div className="font-space text-licht/55 text-xs uppercase tracking-[0.22em] mb-4">
              Project stage
            </div>
            <RadioGroup
              name="rc-stage"
              options={STAGE_OPTIONS}
              value={stage}
              onChange={setStage}
            />
          </div>

          <div className="bg-licht/5 rounded-2xl p-5 sm:p-6 border border-licht/15">
            <div className="font-space text-licht/55 text-xs uppercase tracking-[0.22em] mb-4">
              What happened?
            </div>
            <RadioGroup
              name="rc-event"
              options={EVENT_OPTIONS}
              value={event}
              onChange={setEvent}
            />
          </div>

          {/* Conditional follow-ups */}
          <div className="bg-licht/5 rounded-2xl p-5 sm:p-6 border border-licht/15 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {event === EVENT_TYPE.delay && (
              <NumberField
                id="rc-delay"
                label="Delay length"
                value={delayWeeks}
                onChange={setDelayWeeks}
                suffix="weeks"
                hint="≥ 2 weeks triggers the 10% restart fee."
                labelClassName="text-[#d9d9d9]"
                inputClassName="text-[#d9d9d9] border-[#f97d24]"
                hintClassName="text-[#d9d9d9]"
              />
            )}
            {event === EVENT_TYPE.cancel && stage === PROJECT_STAGE.postMid && (
              <div className="flex flex-col gap-2">
                <label htmlFor="rc-pct" className="font-space text-licht text-xs uppercase tracking-[0.18em]">
                  Production complete
                </label>
                <input
                  id="rc-pct"
                  type="range"
                  min={0}
                  max={100}
                  step={5}
                  value={productionDonePct}
                  onChange={(e) => setProductionDonePct(Number(e.target.value))}
                  className="w-full accent-[#fe9043]"
                />
                <div className="font-space-grotesk font-bold text-licht text-base tabular-nums">
                  {productionDonePct}%
                </div>
                <span className="font-space text-licht/55 text-xs leading-snug">
                  ≥ 50% retains the full midpoint payment.
                </span>
              </div>
            )}
            {isAdmin && (
              <>
                <NumberField
                  id="rc-rev"
                  label="Extra revision rounds"
                  value={extraRevisions}
                  onChange={setExtraRevisions}
                  suffix="× €120"
                />
                <NumberField
                  id="rc-hrs"
                  label="Out-of-window hours"
                  value={outOfWindowHours}
                  onChange={setOutOfWindowHours}
                  suffix="× €75"
                  step={0.25}
                />
              </>
            )}
            {/* Empty hint when no conditional fields */}
            {!isAdmin &&
              event !== EVENT_TYPE.delay &&
              !(event === EVENT_TYPE.cancel && stage === PROJECT_STAGE.postMid) && (
                <span className="font-space text-licht/55 text-sm leading-snug">
                  Nothing else needed for this combination — see the breakdown on the right.
                </span>
              )}
          </div>
        </div>

        {/* Result */}
        <div className="flex flex-col gap-5">
          <div className="rounded-2xl bg-licht text-mainkleur p-6 sm:p-7 lg:p-8 shadow-xl shadow-mainkleur/20 flex flex-col gap-4">
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-space text-mainkleur/55 text-xs uppercase tracking-[0.22em]">
                Estimated outcome
              </span>
              <span className="font-space text-mainkleur/45 text-xs uppercase tracking-[0.18em]">
                Excl. 21% VAT
              </span>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl bg-mainkleur/5 p-3.5 flex flex-col gap-1">
                <span className="font-space text-mainkleur/55 text-[10px] uppercase tracking-[0.18em]">
                  Invoice now
                </span>
                <span className="font-bricolage font-extrabold text-mainkleur text-xl sm:text-2xl tracking-tight tabular-nums">
                  {fmtEuro(result.amountDue)}
                </span>
              </div>
              <div className="rounded-xl bg-mainkleur/5 p-3.5 flex flex-col gap-1">
                <span className="font-space text-mainkleur/55 text-[10px] uppercase tracking-[0.18em]">
                  Refund
                </span>
                <span className="font-bricolage font-extrabold text-emerald-700 text-xl sm:text-2xl tracking-tight tabular-nums">
                  {fmtEuro(result.refundable)}
                </span>
              </div>
              <div className="rounded-xl bg-mainkleur/5 p-3.5 flex flex-col gap-1">
                <span className="font-space text-mainkleur/55 text-[10px] uppercase tracking-[0.18em]">
                  Retained
                </span>
                <span className="font-bricolage font-extrabold text-mainkleur text-xl sm:text-2xl tracking-tight tabular-nums">
                  {fmtEuro(result.retained)}
                </span>
              </div>
            </div>

            <div className="border-t border-mainkleur/15 pt-4">
              <ul className="space-y-2.5">
                {result.lineItems.length === 0 && (
                  <li className="font-space text-mainkleur/65 text-sm leading-snug">
                    Nothing to invoice — the project continues without restart cost.
                  </li>
                )}
                {result.lineItems.map((item, idx) => {
                  const sign = item.amount < 0 ? '−' : '';
                  const tone =
                    item.kind === 'refund'
                      ? 'text-emerald-700'
                      : item.kind === 'retained'
                        ? 'text-mainkleur'
                        : item.kind === 'fee'
                          ? 'text-[#f97b22]'
                          : 'text-mainkleur/55';
                  return (
                    <li key={idx} className="flex items-baseline justify-between gap-3 text-sm sm:text-[15px]">
                      <span className="font-space text-mainkleur leading-snug">
                        {item.label}{' '}
                        {item.policyRef && (
                          <Link
                            to={`/terms${policyHash(item.policyRef)}`}
                            className="text-[#f97b22] hover:text-mainkleur underline underline-offset-2 decoration-1 text-xs"
                          >
                            §
                          </Link>
                        )}
                      </span>
                      <span className={`font-space-grotesk font-bold tabular-nums whitespace-nowrap ${tone}`}>
                        {item.amount === 0 ? '—' : `${sign}${fmtEuro(Math.abs(item.amount))}`}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="border-t border-mainkleur/15 pt-4">
              <p className="font-space text-mainkleur text-sm sm:text-[15px] leading-snug">
                {result.summary}
              </p>
            </div>

            {!isAdmin && (
              <div className="border-t border-mainkleur/15 pt-4 font-space text-mainkleur/55 text-xs leading-snug">
                This is an automated estimate based on the published Terms of Service. The final amount is always confirmed in writing before any invoice or refund is issued.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestartCalculator;
